import { useContext } from 'react'
import GlobalContext from '@/context/GlobalContext'
import { useUserOrganizationQuery } from '@/gql/systemApi'
import { RESTRICTED_USER_ROLES, USER_ROLES } from '@/constants'
import { GlobalPermissionsType, PermissionsMatrixType } from '@/types'

// Cache permissions results to prevent a content jump
// in the app bar when navigating between pages
const permissionsCache = new Map<string, boolean>()
let currentUserId: string | null = null

export const useRestrictedUserPermissions = (
  globalEntity: keyof PermissionsMatrixType,
  permissionsType?: keyof GlobalPermissionsType
) => {
  const globalContext = useContext(GlobalContext)
  const permissions = globalContext?.globalPermissions

  const currentPermissionsSet = permissionsType
    ? permissions?.[permissionsType]
    : permissions?.inOrganization

  const { data: userMe, loading } = useUserOrganizationQuery()
  const roleId = userMe?.users_me?.role?.id
  const userId = userMe?.users_me?.id
  const isOrganizationAccount =
    userMe?.users_me?.account_type?.[0]?.user_id?.account_type?.[0]
      ?.account_type_id?.name !== 'Individual'
  const isCaseManager = roleId === USER_ROLES.CaseManagerUser

  // Clear cache if user changed
  if (userId && currentUserId !== userId) {
    permissionsCache.clear()
    currentUserId = userId
  }

  // Create a cache key based on roleId and globalEntity
  const cacheKey = `${roleId}-${globalEntity}-${
    permissionsType || 'inOrganization'
  }`

  // Check if we have cached result
  const cachedResult = permissionsCache.get(cacheKey)

  // Calculate restrictions if not cached and data is available
  let isRestrictedUser = false
  if (cachedResult !== undefined) {
    isRestrictedUser = cachedResult
  } else if (roleId && permissions) {
    isRestrictedUser =
      RESTRICTED_USER_ROLES.includes(roleId) ||
      !currentPermissionsSet?.[globalEntity]?.read

    // Cache the result
    permissionsCache.set(cacheKey, isRestrictedUser)
  }

  return {
    roleId,
    isRestrictedUser,
    isCaseManager,
    currentPermissionsSet,
    isOrganizationAccount,
    loading,
  }
}
